import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateField } from "../../../reduxSlice/profileSlice";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { postApi } from "../../../services/api/api";

export const validateStep6 = (data) => {
    const errors = {};
    const nameRegex = /^[A-Za-z.\s]+$/;

    // 1. Expectations & Description Validation Rules
    const expectationsText = data.expectations?.trim() || "";
    if (!expectationsText) {
        errors.expectations = "Expectations field is required.";
    }

    if (data.expectations?.length > 50) {
        const descriptionText = data.expectationsDescription?.trim() || "";
        const descWords = descriptionText ? descriptionText.split(/\s+/) : [];

        if (!descriptionText) {
            errors.expectationsDescription = "Please provide the remaining description of your expectations.";
        } else if (descWords.length > 200) {
            errors.expectationsDescription = `Description cannot exceed 200 words (Current word count: ${descWords.length}).`;
        }
    }

    // 2. Phone Validation Core Engine
    const validatePhoneNumber = (phoneVal, dialCodeVal) => {
        if (!phoneVal?.trim()) return "Phone number is required.";

        const cleanDigits = phoneVal.replace(/[^0-9]/g, "");
        const isIndia = dialCodeVal === "91";

        if (isIndia) {
            if (cleanDigits.length !== 10) {
                return "Indian mobile numbers must be exactly 10 digits.";
            }
        } else {
            if (cleanDigits.length < 6 || cleanDigits.length > 15) {
                return "International numbers must be between 6 and 15 digits.";
            }
        }
        return "";
    };

    // Primary Contact Validation Rules
    if (!data.contactName?.trim()) {
        errors.contactName = "Primary contact name is required.";
    } else if (!nameRegex.test(data.contactName)) {
        errors.contactName = "Name must contain alphabets and spaces only.";
    }

    const contactPhoneError = validatePhoneNumber(data.contactNumber, data.contactCountryCode);
    if (contactPhoneError) errors.contactNumber = contactPhoneError;

    // 3. Address Character Length Validation (Max 250 characters)
    const addressText = data.address?.trim() || "";
    if (!addressText) {
        errors.address = "Primary contact address is required.";
    } else if (addressText.length > 250) {
        errors.address = `Address cannot exceed 250 characters (Current length: ${addressText.length}).`;
    }

    // Country Dropdown Check
    if (!data.primaryCountry) {
        errors.primaryCountry = "Country selection is required.";
    }

    // Secondary Contact Validation Rules (Optional, but validated if filled)
    if (data.secContactName?.trim() && !nameRegex.test(data.secContactName)) {
        errors.secContactName = "Secondary name must contain alphabets and spaces only.";
    }

    if (data.secContactNumber?.trim()) {
        const secPhoneError = validatePhoneNumber(data.secContactNumber, data.secContactCountryCode);
        if (secPhoneError) errors.secContactNumber = secPhoneError;
    }

    // 5. Correspondence Address Character Length Validation (Max 250 characters)
    const correspondenceText = data.correspondenceAddress?.trim() || "";
    if (correspondenceText.length > 250) {
        errors.correspondenceAddress = `Secondary contact address cannot exceed 250 characters (Current length: ${correspondenceText.length}).`;
    }

    // 6. Optional Social Links Format Validation
    if (data.facebook?.trim() && !/^(https?:\/\/)?(www\.)?facebook\.com\/.+/i.test(data.facebook)) {
        errors.facebook = "Please enter a valid Facebook link (e.g., facebook.com/username).";
    }
    if (data.linkedin?.trim() && !/^(https?:\/\/)?(www\.)?linkedin\.com\/.+/i.test(data.linkedin)) {
        errors.linkedin = "Please enter a valid LinkedIn link (e.g., linkedin.com/in/username).";
    }
    if (data.instagram?.trim() && !/^(https?:\/\/)?(www\.)?instagram\.com\/.+/i.test(data.instagram)) {
        errors.instagram = "Please enter a valid Instagram link (e.g., instagram.com/username).";
    }

    // 7. Mandatory Declaration Checkbox Validation
    if (!data.declarationAccepted) {
        errors.declarationAccepted = "You must accept the declaration statement to proceed.";
    }

    return errors;
};

const Step6PreferencesContact = () => {
    const dispatch = useDispatch();
    const formData = useSelector((state) => state.profile);
    const errors = useSelector((state) => state.profile.errors || {});

    const descriptionRef = useRef(null);

    // Live indicators tracking state properties
    const [apiCountries, setApiCountries] = useState([]);
    const [isLoadingCountries, setIsLoadingCountries] = useState(false);

    const currentExpectationsLength = formData.expectations ? formData.expectations.length : 0;
    const currentDescWordCount = formData.expectationsDescription?.trim() ? formData.expectationsDescription.trim().split(/\s+/).length : 0;
    const currentAddressLength = formData.address ? formData.address.length : 0;
    const currentCorrespondenceLength = formData.correspondenceAddress ? formData.correspondenceAddress.length : 0;

    // Fetch master country records dynamically using postApi architecture matching your exact schema layout
    useEffect(() => {
        const fetchCountries = async () => {
            setIsLoadingCountries(true);
            try {
                const masterRes = await postApi("/countries", {});
                //const masterRes = await response.json();

                // FIXED: Extracts payload directly from the response 'data' node layout
                const profileData = masterRes?.data || [];

                const formattedCountries = profileData
                    .map(c => ({ country_id: c.country_name, country_name: c.country_name }))
                    .filter(c => c.country_name)
                    .sort((a, b) => a.country_name.localeCompare(b.country_name));

                setApiCountries(formattedCountries);
            } catch (error) {
                console.error("Error executing API helper initialization matrix:", error);
                setApiCountries([
                    { country_id: "India", country_name: "India" },
                    { country_id: "United States", country_name: "United States" },
                    { country_id: "United Kingdom", country_name: "United Kingdom" },
                    { country_id: "United Arab Emirates", country_name: "United Arab Emirates" },
                    { country_id: "Canada", country_name: "Canada" },
                    { country_id: "Australia", country_name: "Australia" }
                ]);
            } finally {
                setIsLoadingCountries(false);
            }
        };

        fetchCountries();
    }, []);

    const handleInputChange = (field, value) => {
        let finalValue = value;

        if (["contactName", "secContactName"].includes(field)) {
            finalValue = finalValue.replace(/[^A-Za-z\s.]/g, "");
        }

        if (["contactName", "secContactName"].includes(field)) {
            if (typeof finalValue === "string") {
                finalValue = finalValue.replace(/\b\w/g, (char) => char.toUpperCase());
            }
        } else if (["expectations", "expectationsDescription", "address", "correspondenceAddress"].includes(field)) {
            if (typeof finalValue === "string" && finalValue.length > 0) {
                finalValue = finalValue.charAt(0).toUpperCase() + finalValue.slice(1);
            }
        }

        dispatch(updateField({ field, value: finalValue }));

        let simulatedData = { ...formData, [field]: finalValue };
        if (field === "expectations" && finalValue.length < 50) {
            dispatch(updateField({ field: "expectationsDescription", value: "" }));
            simulatedData.expectationsDescription = "";
        }

        const generatedErrors = validateStep6(simulatedData);

        dispatch(updateField({
            field: "errors",
            value: {
                ...errors,
                [field]: generatedErrors[field] || "",
                ...(field === "expectations" && finalValue.length < 50 ? { expectationsDescription: "" } : {}),
                ...(field === "expectations" ? { expectationsDescription: generatedErrors.expectationsDescription || "" } : {})
            }
        }));

        if (field === "expectations" && finalValue.length >= 50) {
            setTimeout(() => {
                if (descriptionRef.current) {
                    descriptionRef.current.focus();
                }
            }, 10);
        }
    };

    const handleCountrySelect = (e) => {
        const selectedValue = e.target.value;
        // Finds name and id mapping elements cleanly
        const selectedObj = apiCountries.find(c => String(c.country_id) === String(selectedValue) || c.country_name === selectedValue);

        const countryName = selectedObj ? selectedObj.country_name : selectedValue;
        const countryId = selectedObj ? selectedObj.country_id : "";

        dispatch(updateField({ field: "primaryCountry", value: countryName }));
        dispatch(updateField({ field: "primaryCountryId", value: countryId }));

        const simulatedData = { ...formData, primaryCountry: countryName, primaryCountryId: countryId };
        const generatedErrors = validateStep6(simulatedData);

        dispatch(updateField({
            field: "errors",
            value: {
                ...errors,
                primaryCountry: generatedErrors.primaryCountry || ""
            }
        }));
    };

    const handlePhoneChange = (field, value, countryData) => {
        const dialCode = countryData.dialCode || "";
        let localNumber = "";
        if (value.startsWith(dialCode)) {
            localNumber = value.slice(dialCode.length).replace(/[^0-9]/g, "");
        } else {
            localNumber = "";
        }

        if (dialCode === "91") {
            localNumber = localNumber.slice(0, 10);
        } else {
            localNumber = localNumber.slice(0, 15);
        }

        const codeField = field === "contactNumber" ? "contactCountryCode" : "secContactCountryCode";

        dispatch(updateField({ field, value: localNumber }));
        dispatch(updateField({ field: codeField, value: dialCode }));

        const simulatedData = {
            ...formData,
            [field]: localNumber,
            [codeField]: dialCode
        };
        const generatedErrors = validateStep6(simulatedData);

        dispatch(updateField({
            field: "errors",
            value: {
                ...errors,
                [field]: generatedErrors[field] || ""
            }
        }));
    };

    const getPhoneInputValue = (number, code) => {
        if (!number) return "";
        const cleanCode = String(code || "").replace(/[^0-9]/g, "");
        const cleanNum = String(number).replace(/[^0-9]/g, "");
        return `${cleanCode}${cleanNum}`;
    };

    return (
        <div className="form-animate">
            <div className="row">
                {/* Expectations Field */}
                <div className="col-md-12">
                    <div className="inputcontainer mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="input_label">What are your expectations from your life partner? <span className="m_feild">*</span></h5>
                            <small className="text-muted" style={{ fontSize: "11px" }}>
                                {currentExpectationsLength} / 50 chars
                            </small>
                        </div>
                        <input
                            type="text"
                            maxLength="50"
                            className={`form-control input_style ${errors.expectations ? "is-invalid" : ""}`}
                            placeholder="Enter short overview of expectations"
                            value={formData.expectations || ""}
                            onChange={(e) => handleInputChange("expectations", e.target.value)}
                        />
                        <span className="note">(Only first 50 characters captured in this box will be printed in the print version of the suchi.)</span>
                        {errors.expectations && <div className="text-danger small mt-1">{errors.expectations}</div>}
                    </div>
                </div>

                {/* Conditional Expectations Description Field */}
                {currentExpectationsLength >= 50 && (
                    <div className="col-md-12 animate-fade-in">
                        <div className="inputcontainer mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="input_label">Detailed Expectations Description </h5>
                                <small className={`text-muted ${currentDescWordCount > 200 ? "text-danger" : ""}`} style={{ fontSize: "11px" }}>
                                    {currentDescWordCount} / 200 words
                                </small>
                            </div>
                            <textarea
                                ref={descriptionRef}
                                rows="3"
                                className={`form-control input_style text-area-control ${errors.expectationsDescription ? "is-invalid" : ""}`}
                                placeholder="Continue typing your detailed partner expectations here..."
                                value={formData.expectationsDescription || ""}
                                onChange={(e) => handleInputChange("expectationsDescription", e.target.value)}
                            />
                            <p className="note">(Additional expectations information in this box will be available and visible in online profile search only and will not be printed in the print version of the suchi book.)</p>
                            {errors.expectationsDescription && (
                                <div className="text-danger small mt-1 fw-bold">{errors.expectationsDescription}</div>
                            )}
                        </div>
                    </div>
                )}

                {/* Horoscope Matching Checkbox */}
                <div className="col-md-12">
                    <div className="inputcontainer mb-4">
                        <div className="form-check m-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="matchHoroscope"
                                checked={formData.matchHoroscope || false}
                                onChange={(e) => handleInputChange("matchHoroscope", e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="matchHoroscope">Would you like to match horoscope?</label>
                        </div>
                    </div>
                </div>

                <div className="col-md-12">
                    <div className="form_section_heading mb-3">
                        <h5>Family contact details</h5>
                    </div>
                </div>

                {/* Primary Contact Name */}
                <div className="col-md-6">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Primary Contact Name <span className="m_feild">*</span></h5>
                        <input
                            type="text"
                            maxLength="60"
                            className={`form-control input_style text-capitalize ${errors.contactName ? "is-invalid" : ""}`}
                            placeholder="Enter Primary Contact Name"
                            value={formData.contactName || ""}
                            onChange={(e) => handleInputChange("contactName", e.target.value)}
                        />
                        {errors.contactName && <div className="text-danger small mt-1">{errors.contactName}</div>}
                    </div>
                </div>

                {/* Primary Contact Number */}
                <div className="col-md-6">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Primary Contact Number <span className="m_feild">*</span></h5>
                        <PhoneInput
                            country={"in"}
                            value={getPhoneInputValue(formData.contactNumber, formData.contactCountryCode || "91")}
                            onChange={(value, country) => handlePhoneChange("contactNumber", value, country)}
                            inputClass={`form-control input_style w-100 ${errors.contactNumber ? "is-invalid" : ""}`}
                            containerClass="w-100 dropdown_phone_container"
                            inputProps={{
                                name: "contactNumber",
                                required: true,
                                autoFocus: false
                            }}
                        />
                        {errors.contactNumber && <div className="text-danger small mt-1">{errors.contactNumber}</div>}
                    </div>
                </div>

                {/* Contact Physical Address */}
                <div className="col-md-12">
                    <div className="inputcontainer mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="input_label">Primary Address <span className="m_feild">*</span></h5>
                            <small className={`text-muted ${currentAddressLength > 250 ? "text-danger" : ""}`} style={{ fontSize: "11px" }}>
                                {currentAddressLength} / 250 chars
                            </small>
                        </div>
                        <textarea
                            rows="3"
                            maxLength="250"
                            className={`form-control input_style ${errors.address ? "is-invalid" : ""}`}
                            placeholder="Enter Primary Address (Maximum 250 characters)"
                            value={formData.address || ""}
                            onChange={(e) => handleInputChange("address", e.target.value)}
                        />
                        {errors.address && <div className="text-danger small mt-1">{errors.address}</div>}
                    </div>
                </div>

                {/* FIXED: Standard HTML Country Dropdown Selection Box (Mimics Step 1 Select inputs pattern) */}
                <div className="col-md-12">
                    <div className="inputcontainer mb-3">
                        <div className="d-flex align-items-center gap-2">
                            <h5 className="input_label m-0">Country <span className="m_feild">*</span></h5>
                            {isLoadingCountries && <div className="spinner-border spinner-border-sm text-secondary" role="status"></div>}
                        </div>
                        <select
                            className={`form-select input_style ${errors.primaryCountry ? "is-invalid" : ""}`}
                            value={formData.primaryCountryId || formData.primaryCountry || ""}
                            onChange={handleCountrySelect}
                        >
                            <option value="">Select Country</option>
                            {apiCountries.map((opt) => (
                                <option key={opt.country_id} value={opt.country_id}>
                                    {opt.country_name}
                                </option>
                            ))}
                        </select>
                        {errors.primaryCountry && <div className="text-danger small mt-1">{errors.primaryCountry}</div>}
                    </div>
                </div>

                {/* Secondary Contact Name */}
                <div className="col-md-6">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Secondary Contact Name</h5>
                        <input
                            type="text"
                            maxLength="60"
                            className={`form-control input_style text-capitalize ${errors.secContactName ? "is-invalid" : ""}`}
                            placeholder="Enter Secondary Contact Name"
                            value={formData.secContactName || ""}
                            onChange={(e) => handleInputChange("secContactName", e.target.value)}
                        />
                        {errors.secContactName && <div className="text-danger small mt-1">{errors.secContactName}</div>}
                    </div>
                </div>

                {/* Secondary Contact Number */}
                <div className="col-md-6">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Secondary Contact Number</h5>
                        <PhoneInput
                            country={"in"}
                            value={getPhoneInputValue(formData.secContactNumber, formData.secContactCountryCode || "91")}
                            onChange={(value, country) => handlePhoneChange("secContactNumber", value, country)}
                            inputClass={`form-control input_style w-100 ${errors.secContactNumber ? "is-invalid" : ""}`}
                            containerClass="w-100 dropdown_phone_container"
                            inputProps={{
                                name: "secContactNumber",
                                required: false,
                                autoFocus: false
                            }}
                        />
                        {errors.secContactNumber && <div className="text-danger small mt-1">{errors.secContactNumber}</div>}
                    </div>
                </div>

                {/* Secondary Address (Correspondence) */}
                <div className="col-md-12">
                    <div className="inputcontainer mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="input_label">Secondary Address</h5>
                            <small className={`text-muted ${currentCorrespondenceLength > 250 ? "text-danger" : ""}`} style={{ fontSize: "11px" }}>
                                {currentCorrespondenceLength} / 250 chars
                            </small>
                        </div>
                        <textarea
                            rows="3"
                            maxLength="250"
                            className={`form-control input_style ${errors.correspondenceAddress ? "is-invalid" : ""}`}
                            placeholder="Enter Secondary Address (Maximum 250 characters)"
                            value={formData.correspondenceAddress || ""}
                            onChange={(e) => handleInputChange("correspondenceAddress", e.target.value)}
                        />
                        {errors.correspondenceAddress && <div className="text-danger small mt-1">{errors.correspondenceAddress}</div>}
                    </div>
                </div>

                {/* Dynamic Social Links Iterations */}
                {["facebook", "linkedin", "instagram"].map((net) => (
                    <div className="col-md-12" key={net}>
                        <div className="inputcontainer mb-3">
                            <h5 className="input_label">
                                {net === "linkedin" ? "LinkedIn" : net.charAt(0).toUpperCase() + net.slice(1)} Link
                            </h5>
                            <input
                                className={`form-control input_style ${errors[net] ? "is-invalid" : ""}`}
                                placeholder={`Enter ${net === "linkedin" ? "LinkedIn" : net.charAt(0).toUpperCase() + net.slice(1)} URL`}
                                value={formData[net] || ""}
                                onChange={(e) => handleInputChange(net, e.target.value)}
                            />
                            {errors[net] && <div className="text-danger small mt-1">{errors[net]}</div>}
                        </div>
                    </div>
                ))}

                {/* Mandatory Declaration Checkbox block */}
                <div className="col-md-12">
                    <div className="inputcontainer mb-3">
                        <div className="form-check m-0 d-flex align-items-start gap-2">
                            <input
                                className={`form-check-input mt-1 ${errors.declarationAccepted ? "is-invalid" : ""}`}
                                type="checkbox"
                                id="declarationAccepted"
                                checked={formData.declarationAccepted || false}
                                onChange={(e) => handleInputChange("declarationAccepted", e.target.checked)}
                            />
                            <label className="form-check-label text-muted small" htmlFor="declarationAccepted" style={{ lineHeight: "1.6", cursor: "pointer" }}>
                                I hereby declare that I am creating and managing this profile myself, and all the details provided are true and accurate to the best of my knowledge.
                            </label>
                        </div>
                        {errors.declarationAccepted && <div className="text-danger small mt-1 fw-bold">{errors.declarationAccepted}</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Step6PreferencesContact;